import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, LogOut, Loader2, X, ShieldCheck, Upload, ImageOff, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, getToken, setToken, clearToken } from "@/lib/api";
import { normalizeImage } from "@/lib/image";
import Header from "@/components/Header";
import { AdminLogs } from "@/components/AdminLogs";
import { AdminBroadcast } from "@/components/AdminBroadcast";
import { AdminBlockedUsers } from "@/components/AdminBlockedUsers";

const TABS = [
  { key: "apps", label: "Приложения", test: "admin-tab-apps" },
  { key: "collections", label: "Подборки", test: "admin-tab-collections" },
  { key: "categories", label: "Категории", test: "admin-tab-categories" },
  { key: "accounts", label: "Аккаунты", test: "admin-tab-accounts" },
  { key: "blocked", label: "🚫 Заблокированные пользователи", test: "admin-tab-blocked" },
  { key: "logs", label: "Логи", test: "admin-tab-logs" },
  { key: "broadcast", label: "Рассылка", test: "admin-tab-broadcast" },
];

const FIELDS = {
  apps: [
    ["name", "Название", "text"],
    ["developer", "Разработчик", "text"],
    ["subtitle", "Подзаголовок", "text"],
    ["category", "Категория", "text"],
    ["rating", "Рейтинг", "number"],
    ["ratings_count", "Кол-во оценок", "text"],
    ["icon", "Иконка (URL/путь)", "text"],
    ["screenshots", "Скриншоты", "images"],
    ["description", "Описание", "textarea"],
    ["whats_new", "Что нового", "textarea"],
    ["size", "Размер", "text"],
    ["age", "Возраст", "text"],
    ["badge", "Бейдж (Мод / PRO / …)", "text"],
    ["featured", "В избранном (hero)", "bool"],
    ["popular", "Популярное", "bool"],
    ["order", "Порядок", "number"],
  ],
  collections: [
    ["title", "Название", "text"],
    ["subtitle", "Подзаголовок", "text"],
    ["order", "Порядок", "number"],
    ["app_ids", "Приложения", "apps"],
  ],
  categories: [
    ["name", "Название", "text"],
    ["emoji", "Эмодзи", "text"],
    ["color", "Цвет (hex)", "text"],
    ["order", "Порядок", "number"],
  ],
  accounts: [
    ["apple_id", "Apple ID", "text"],
    ["password", "Пароль", "text"],
    ["note", "Заметка", "textarea"],
    ["active", "Активен", "bool"],
  ],
};

function Thumb({ src, index, onRemove, onMove, last }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="relative shrink-0 w-24" data-testid={`shot-item-${index}`}>
      {broken ? (
        <div className="w-24 h-40 rounded-xl border border-red-500/40 bg-red-500/10 grid place-items-center text-[10px] text-red-300 text-center p-1" data-testid={`shot-broken-${index}`}>
          <ImageOff size={18} /> не открывается
        </div>
      ) : (
        <img src={src} alt="" onError={() => setBroken(true)} className="w-24 h-40 rounded-xl object-cover border border-white/10" />
      )}
      <button type="button" onClick={onRemove} data-testid={`shot-remove-${index}`} className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/70 text-red-300">
        <X size={14} />
      </button>
      <div className="mt-1 flex justify-between">
        <button type="button" disabled={index === 0} onClick={() => onMove(-1)} data-testid={`shot-left-${index}`} className="w-8 h-6 grid place-items-center rounded-lg bg-white/5 disabled:opacity-30">
          <ChevronLeft size={14} />
        </button>
        <button type="button" disabled={last} onClick={() => onMove(1)} data-testid={`shot-right-${index}`} className="w-8 h-6 grid place-items-center rounded-lg bg-white/5 disabled:opacity-30">
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function ImagesField({ value, onChange }) {
  const move = (i, d) => {
    const next = [...value];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(() => next);
  };
  return (
    <div className="mt-1 space-y-2" data-testid="field-screenshots">
      {value.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {value.map((src, i) => (
            <Thumb key={src + i} src={src} index={i} last={i === value.length - 1}
              onRemove={() => onChange((cur) => cur.filter((_, j) => j !== i))}
              onMove={(d) => move(i, d)} />
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-500" data-testid="shots-empty">Скриншотов пока нет</div>
      )}
      <UploadButton multiple testid="upload-shots" label="Добавить скриншоты" onUploaded={(urls) => onChange((cur) => [...cur, ...urls])} />
    </div>
  );
}

function UploadButton({ multiple, onUploaded, label, testid = "upload" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    for (const f of files) {
      try {
        const { url } = await api.uploadImage(await normalizeImage(f));
        urls.push(url);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || `Ошибка загрузки «${f.name}»`);
      }
    }
    if (urls.length) {
      onUploaded(urls);
      toast.success(`Загружено: ${urls.length}. Нажмите «Сохранить», чтобы применить`);
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={onChange}
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testid}-button`}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/15 text-blue-300 border border-blue-500/30 text-sm active:scale-95 transition disabled:opacity-60"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {label}
      </button>
    </>
  );
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api.login(email, password);
      setToken(data.access_token);
      onLogin();
      toast.success("Вход выполнен");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка входа");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 pt-20 ambient">
      <div className="relative z-10 max-w-sm mx-auto">
        <div className="w-14 h-14 grid place-items-center rounded-2xl bg-blue-500/15 border border-blue-500/30 mb-4">
          <ShieldCheck className="text-blue-400" />
        </div>
        <h1 className="font-display text-2xl font-extrabold">Админка AppWay</h1>
        <p className="text-sm text-slate-400 mb-6">Войдите, чтобы управлять каталогом.</p>
        <form onSubmit={submit} className="space-y-3">
          <input
            data-testid="admin-login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-blue-500/50"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Пароль"
            className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-blue-500/50"
          />
          <button
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full py-3 rounded-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 glow-blue active:scale-95 transition flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={18} className="animate-spin" />} Войти
          </button>
        </form>
      </div>
    </div>
  );
}

function Editor({ type, item, apps, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const base = {};
    FIELDS[type].forEach(([k, , t]) => {
      let v = item ? item[k] : undefined;
      if (t === "csv") v = Array.isArray(v) ? v.join(", ") : v || "";
      else if (t === "apps" || t === "images") v = Array.isArray(v) ? [...v] : [];
      else if (t === "bool") v = !!v;
      else if (t === "number") v = v ?? 0;
      else v = v ?? "";
      base[k] = v;
    });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const set = (k, v) => { setDirty(true); setForm((f) => ({ ...f, [k]: v })); };
  const update = (k, fn) => { setDirty(true); setForm((f) => ({ ...f, [k]: fn(f[k]) })); };
  const close = () => {
    if (dirty && !window.confirm("Есть несохранённые изменения. Закрыть без сохранения?")) return;
    onClose();
  };

  const save = async () => {
    setSaving(true);
    const body = { ...(item || {}), ...form };
    delete body._id;
    FIELDS[type].forEach(([k, , t]) => {
      if (t === "csv") body[k] = String(form[k] || "").split(",").map((s) => s.trim()).filter(Boolean);
      else if (t === "number") body[k] = Number(form[k]) || 0;
    });
    try {
      await api.save(type, item?.id, body);
      toast.success("Сохранено");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      className="fixed inset-0 z-[60] bg-[#0b0e14] max-w-[480px] mx-auto overflow-y-auto"
      data-testid="admin-editor"
    >
      <div className="sticky top-0 glass border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={close} data-testid="admin-editor-close" className="w-9 h-9 grid place-items-center rounded-full bg-white/5">
          <X size={20} />
        </button>
        <div className="font-display font-bold">{item ? "Редактирование" : "Новая запись"}</div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-editor-save"
          className="ml-auto px-5 py-2 rounded-full font-semibold bg-gradient-to-r from-blue-600 to-blue-500 glow-blue active:scale-95 transition flex items-center gap-2"
        >
          {saving && <Loader2 size={16} className="animate-spin" />} Сохранить
        </button>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {FIELDS[type].map(([k, label, t]) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-wide text-slate-400 font-bold">{label}</label>
            {t === "textarea" ? (
              <textarea
                data-testid={`field-${k}`}
                value={form[k]}
                onChange={(e) => set(k, e.target.value)}
                rows={3}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-blue-500/50 text-sm"
              />
            ) : t === "bool" ? (
              <button
                data-testid={`field-${k}`}
                onClick={() => set(k, !form[k])}
                className={`mt-1 block w-14 h-8 rounded-full transition relative ${form[k] ? "bg-blue-500" : "bg-white/15"}`}
              >
                <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition ${form[k] ? "left-7" : "left-1"}`} />
              </button>
            ) : t === "images" ? (
              <ImagesField value={form[k]} onChange={(fn) => update(k, fn)} />
            ) : t === "apps" ? (
              <div className="mt-1 max-h-48 overflow-y-auto rounded-xl bg-white/5 border border-white/10 p-2 space-y-1">
                {apps.map((a) => {
                  const on = form[k].includes(a.id);
                  return (
                    <button
                      key={a.id}
                      data-testid={`field-app-${a.id}`}
                      onClick={() => set(k, on ? form[k].filter((x) => x !== a.id) : [...form[k], a.id])}
                      className={`w-full text-left text-sm px-3 py-2 rounded-lg transition ${on ? "bg-blue-500/20 text-blue-200" : "bg-white/0 text-slate-300"}`}
                    >
                      {on ? "✓ " : ""}{a.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input
                data-testid={`field-${k}`}
                type={t === "number" ? "number" : "text"}
                value={form[k]}
                onChange={(e) => set(k, e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-blue-500/50 text-sm"
              />
            )}
            {k === "icon" && (
              <div className="mt-2 flex items-center gap-3">
                {form.icon && (
                  <img key={form.icon} src={form.icon} alt="" data-testid="icon-preview" className="w-12 h-12 rounded-xl object-cover border border-white/10" />
                )}
                <UploadButton testid="upload-icon" label="Загрузить иконку" onUploaded={(urls) => set("icon", urls[0])} />
              </div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function AdminView() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("apps");
  const [items, setItems] = useState([]);
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [stats, setStats] = useState(null);

  const check = useCallback(() => {
    if (!getToken()) return setAuthed(false);
    api.me().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  useEffect(() => { check(); }, [check]);

  const loadList = useCallback(async () => {
    try {
      if (tab === "apps") setItems(await api.apps());
      else if (tab === "collections") setItems(await api.collections());
      else if (tab === "categories") setItems(await api.categories());
      else if (tab === "accounts") setItems(await api.adminAccounts());
      else if (tab === "logs" || tab === "broadcast" || tab === "blocked") setItems([]);
      setApps(await api.apps());
      setStats(await api.stats());
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); setAuthed(false); }
    }
  }, [tab]);

  useEffect(() => { if (authed) loadList(); }, [authed, loadList]);

  if (authed === null) return <div className="min-h-screen" />;
  if (!authed) return <LoginForm onLogin={check} />;

  const title = (it) => it.name || it.title || it.apple_id;

  const del = async (it) => {
    if (!window.confirm(`Удалить «${title(it)}»?`)) return;
    try {
      await api.remove(tab, it.id);
      toast.success("Удалено");
      loadList();
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  return (
    <div className="pb-28">
      <Header title="Админка" subtitle="Управление каталогом" />
      <div className="px-4 pt-3">
        {stats && (
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[["Апп", stats.apps], ["Подб.", stats.collections], ["Кат.", stats.categories], ["Акк.", stats.accounts]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-white/5 border border-white/10 py-2 text-center">
                <div className="font-display font-bold">{v}</div>
                <div className="text-[10px] text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={t.test}
              onClick={() => setTab(t.key)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition ${tab === t.key ? "bg-blue-500 text-white" : "bg-white/5 text-slate-400"}`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={() => { clearToken(); setAuthed(false); }}
            data-testid="admin-logout"
            className="shrink-0 ml-auto px-3 py-2 rounded-full bg-white/5 text-slate-400"
          >
            <LogOut size={16} />
          </button>
        </div>

        {tab === "logs" && <AdminLogs />}
        {tab === "broadcast" && <AdminBroadcast />}
        {tab === "blocked" && <AdminBlockedUsers />}
        {tab !== "logs" && tab !== "broadcast" && tab !== "blocked" && (
        <>
        <button
          data-testid="admin-add-button"
          onClick={() => setEditing(null)}
          className="w-full mb-3 py-3 rounded-2xl font-semibold bg-white/5 border border-dashed border-white/15 flex items-center justify-center gap-2 active:scale-[0.99] transition"
        >
          <Plus size={18} /> Добавить
        </button>

        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} data-testid={`admin-item-${it.id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/10">
              {it.icon && <img src={it.icon} alt="" className="w-10 h-10 rounded-xl object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{title(it)}</div>
                <div className="text-xs text-slate-500 truncate">
                  {tab === "apps" && `${it.category} · ${it.rating}★`}
                  {tab === "collections" && `${(it.app_ids || it.apps || []).length} прил.`}
                  {tab === "categories" && it.emoji}
                  {tab === "accounts" && `${it.active ? `активен · выдан ${it.issued_count}×` : "выключен"}${it.source === "bot" ? " · из бота" : ""}`}
                </div>
              </div>
              <button data-testid={`admin-edit-${it.id}`} onClick={() => setEditing(it)} className="w-9 h-9 grid place-items-center rounded-xl bg-white/5">
                <Pencil size={16} />
              </button>
              <button data-testid={`admin-delete-${it.id}`} onClick={() => del(it)} className="w-9 h-9 grid place-items-center rounded-xl bg-red-500/15 text-red-300">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        </>
        )}
      </div>

      {editing !== undefined && (
        <Editor
          type={tab}
          item={editing}
          apps={apps}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); loadList(); }}
        />
      )}
    </div>
  );
}
